import { useCallback, useEffect, useRef, useState } from 'react';
import { axios } from '@config/axios';
import Environment from '@config/index';
import { usePreferences } from '@context/preferences.context';
import { CMS_PAGES, ContentStackContainers, ContentStackPageNames } from '@enums/contentStack';
import { convertResponseData } from '@helpers/utils/cms/cms-fields-mapping';
import { getLocaleByLanguage } from '@helpers/localization';
import { SupportedLanguages } from '@interfaces/models/language';
import logger from '@helpers/utils/logger/client';

interface ILivePreviewEvent {
  content_type_uid: string;
  hash: string;
}

interface ILivePreviewAckEvent {
  entryUid: string;
  contentTypeUid: string;
}

interface ILivePreviewEventData {
  data: ILivePreviewAckEvent | ILivePreviewEvent;
  from: string;
  type: string;
}

interface IEvent {
  data: ILivePreviewEventData;
}

const useContentStackLivepreview = (callBack: (p: unknown) => void) => {
  const entryIdRef = useRef<string>(null);
  const [isLivePreviewModeEnabled, setLivePreviewModeState] = useState<boolean>(false);
  const [hasLivePreviewError, setHasLivePreviewError] = useState<boolean>(false);
  const isProductionMode = process.env.REACT_APP_ENVIRONMENT === 'prod';
  const { locale, siteId, currency, language, country } = usePreferences();

  const callBackRef = useRef(callBack);
  callBackRef.current = callBack;

  const preferencesRef = useRef({ locale, siteId, currency, language, country });
  preferencesRef.current = { locale, siteId, currency, language, country };

  const getLivePreviewContent = useCallback(async (contentType: string, hash: string) => {
    const { locale: loc, siteId: sid, currency: cur, language: lang, country: ctr } = preferencesRef.current;

    const pageParams = new URLSearchParams(window.location.search);
    const setLocaleParam = pageParams.get('setLocale');
    const setLocaleLanguage = setLocaleParam?.split('.')[1];
    const setLocaleCountry = setLocaleLanguage
      ? getLocaleByLanguage(setLocaleLanguage as SupportedLanguages)
          ?.split('-')[1]
          ?.toUpperCase()
      : null;
    const effectiveCountry = setLocaleCountry || ctr;

    const reservedKeys = new Set([
      'entryId',
      'contentType',
      'hash',
      'locale',
      'country',
      'setLocale',
      'live_preview',
      'csBranch',
    ]);

    try {
      const entryId = entryIdRef.current;
      const url = new URL(
        `/contents/cms/cs/entries/${encodeURIComponent(entryId)}/content_types/${encodeURIComponent(
          contentType,
        )}/hash/${encodeURIComponent(hash)}`,
        Environment.apiBaseUrl,
      );
      url.searchParams.set('locale', loc.toLocaleLowerCase());
      url.searchParams.set('country', effectiveCountry);

      pageParams.forEach((value, key) => {
        if (!reservedKeys.has(key)) {
          url.searchParams.set(key, value);
        }
      });

      const response = await axios.get(url.toString(), {
        timeout: 45000,
        headers: {
          'x-lang-code': loc.toLocaleLowerCase(),
          'x-country': effectiveCountry,
          'x-vc-siteid': String(sid),
          'x-vc-language': lang,
          'x-vc-currency': cur,
          'x-consumer-custom-id': 'anonymous',
          'x-device-brand': 'other',
          'x-device-type': 'desktop',
          'x-support-type': 'web',
          'cs-branch': pageParams.get('csBranch') || 'main',
        },
      });

      const livepreviewResponse = convertResponseData(response?.data?.data) || null;

      if (!livepreviewResponse) {
        return null;
      }

      if (CMS_PAGES.includes(livepreviewResponse?.contentTypeId)) {
        return livepreviewResponse;
      }

      return {
        [ContentStackContainers[contentType]]: livepreviewResponse,
        contentTypeId: ContentStackPageNames.LIVEPREVIEW_PAGE,
      };
    } catch (err) {
      logger.error(err, 'Error occurred at action: getLivePreviewContent - use-contentstack-livepreview.ts');
      return null;
    }
  }, []);

  const onEventListener = useCallback(
    async (e: IEvent) => {
      const { from, type } = e?.data || {};

      if (from !== 'live-preview') {
        return;
      }

      if (type === 'init-ack') {
        const data = e?.data?.data as ILivePreviewAckEvent;
        entryIdRef.current = data?.entryUid;
        setLivePreviewModeState(true);
      } else if (type === 'client-data-send') {
        const { hash, content_type_uid: contentTypeUid } = e?.data?.data as ILivePreviewEvent;

        if (!hash || !entryIdRef.current) {
          return;
        }

        setHasLivePreviewError(false);
        const previewData = await getLivePreviewContent(contentTypeUid, hash);
        if (previewData) {
          callBackRef.current(previewData);
        } else {
          setHasLivePreviewError(true);
        }
      }
    },
    [getLivePreviewContent],
  );

  const eventListenerRef = useRef(onEventListener);
  eventListenerRef.current = onEventListener;

  const stableListener = useCallback((e: MessageEvent) => {
    if (e.origin !== 'https://eu-app.contentstack.com') {
      return;
    }
    eventListenerRef.current(e as unknown as IEvent);
  }, []);

  useEffect(() => {
    if (isProductionMode) {
      return;
    }

    const cmsDomain = 'https://eu-app.contentstack.com';
    const isInIframe = window.parent !== window;
    if (!isInIframe) {
      return;
    }

    const currentOrigin = window?.location?.origin;
    const localOrigin = 'http://localhost:3000';
    const allowedDomain = 'vestiairecollective.com';

    if (currentOrigin !== localOrigin && !currentOrigin.endsWith(`.${allowedDomain}`)) {
      logger.error('CMS Live Preview Unauthorized Origin:::', currentOrigin);
      return;
    }

    window.parent.postMessage(
      {
        type: 'init',
        from: 'live-preview',
        data: {
          config: {
            shouldReload: false,
            href: window.location.href,
          },
        },
      },
      cmsDomain,
    );

    window.addEventListener('message', stableListener);

    return () => {
      window.removeEventListener('message', stableListener);
    };
  }, [isProductionMode, stableListener]);

  return {
    isLivePreviewModeEnabled,
    hasLivePreviewError,
  };
};

export default useContentStackLivepreview;
